import { inject, Injectable, Injector } from '@angular/core';
import { DashboardService } from '../pages/dashboard/dashboard-component/service/dashboard.service';
import { AppStateService } from '../services/state-store/app-state';
import { Router } from '@angular/router';
import { PinStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { DashboardStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { Subscription } from 'rxjs';
import { faker } from '@faker-js/faker';
import { BizEntityFunctionTypes, BizEntityTypeCategory, IBizEntity, LoggerService } from '@optima-stride-ai/core';
import { ContextMenuItem, ToastService } from '@optima-stride-ai/optima-components';
import { iDashboard, iLayoutList, iContentDimensions, iDashboardItem } from '@optima-stride-ai/optima-app-framework';
import { iPinnedItemContainer, PinItemContainerType, PinItemLocations } from '@optima-stride-ai/optima-app-framework';
import { BizEntityTypeStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { BizEntityStateStoreService } from '@optima-stride-ai/optima-app-framework';
import { GlobalStateService } from '../../service/global.service';
import { AuthenticationStubService } from '../../platform-stubs/authentication-stub';
import { OptimaDefaultModalService } from '../components/modal 2/modal.service';
import { IModalConfig, ModalDialogs, ModalDialogSizes } from '../components/modal 2/modal';
import { ContextMenuIcons, ContextMenuLabels, FileExtensions, UILabels, Routes } from '../shared/enums/app-constants';
import { EntityFileUrlService } from '../services/entity-file-url.service';
import { EntityDeleteService } from '../shared/utils/entity-delete.util';
import { RelationshipStateStoreService, XappCapHandlerService } from '@optima-stride-ai/optima-app-framework';
import { LibraryMenuService } from '../pages/libraries/services/library-menu.service';
@Injectable({
  providedIn: 'root',
})
export class ContextMenuService {
  private dashboardService: DashboardService = inject(DashboardService)
  private appStateService = inject(AppStateService);
  private router = inject(Router);
  private toastService = inject(ToastService);
  private bizEntityStateStoreService = inject(BizEntityStateStoreService);
  private bizEntityTypeStateStoreService = inject(BizEntityTypeStateStoreService);
  private pinStateStoreService: PinStateStoreService = inject(PinStateStoreService);
  private dashboardStateStoreService: DashboardStateStoreService = inject(DashboardStateStoreService);
  private logger = inject(LoggerService);
  private authService = inject(AuthenticationStubService);
  private modalService = inject(OptimaDefaultModalService);
  private readonly entityFileUrlService = inject(EntityFileUrlService);
  private readonly entityDeleteService = inject(EntityDeleteService);
  private readonly relationshipState = inject(RelationshipStateStoreService);
  private readonly xappCapHandlerService = inject(XappCapHandlerService);
  private readonly injector = inject(Injector);
  
  constructor(private global: GlobalStateService) {
    this.subscribeToStates();
    this.populateDashboardListFromRemote();
  }
  dashboardStateSubscription: Subscription | undefined;
  
  /**
   * Get context menu items (synchronous version - doesn't include Discussion submenu)
   */
  getMenu(data?: any): ContextMenuItem[] {
    const entity = data && typeof data === 'object' ? data : undefined;
    return this.getOptionMenuSubList(this.getContextMenuItemList(data), false, 0, entity);
  }

  /**
   * Get context menu items with Discussion submenu support (async version)
   * NOTE: This is deprecated - use getMenuAsync with proper await handling
   */
  async getMenuAsync(data?: any, includeDiscussion: boolean = true): Promise<ContextMenuItem[]> {
    const entity = data && typeof data === 'object' ? data : undefined;
    const menuItems = await this.getContextMenuItemListAsync(data, includeDiscussion);
    return this.getOptionMenuSubList(menuItems, false, 0, entity);
  }

  /**
   * Get initial menu synchronously, then enhance with async data
   * This allows menu to appear immediately without blocking
   */
  async getMenuWithAsyncEnhancement(data?: any, onUpdate?: (items: ContextMenuItem[]) => void): Promise<ContextMenuItem[]> {
    const entity = data && typeof data === 'object' ? data : undefined;
    
    // 1. Provide sync menu immediately (without New submenu)
    const syncMenuItems = this.getMenu(data);
    
    // 2. Return sync items immediately
    // 3. Then enhance async in background if callback provided
    if (onUpdate) {
      this.getContextMenuItemListAsync(data)
        .then((asyncMenuItems) => {
          const enhancedMenuItems = this.getOptionMenuSubList(asyncMenuItems, false, 0, entity);
          onUpdate(enhancedMenuItems);
        })
        .catch((error) => {
          this.logger.error(`Error enhancing menu with async data: ${error}`);
        });
    }
    
    return syncMenuItems;
  }

  async deleteBizEntity(entity: any) {
    this.entityDeleteService.launchDeleteDialog(entity);
  }

  updateBizEntityName(data: any) {
    // Use the currently selected entity from app state (like Open and Delete do)
    const entity = this.appStateService.selectedBizEntityForEdit;
    if (!entity) {
      this.logger.error('No entity selected for rename');
      return;
    }

    this.logger.info(`✏️ Renaming entity: ${entity.name || entity.id}`);

    const config: IModalConfig = {
      dialog: ModalDialogs.RenameDialogue,
      title: 'Rename',
      backdrop: 'static',
      alignment: 'center',
      id: new Date().getTime().toString(),
      size: ModalDialogSizes.FULL_SCREEN,
      titleSize: '1.5rem' as any,
      model: {
        in: {
          message: '',
          bizEntity: entity
        },
        out: null
      },
      footerButtons: [
        {
          id: 'cancel-rename-btn',
          label: 'Cancel',
          color: 'secondary',
          closesModal: true,
          click: () => {
            this.logger.info('❌ Rename cancelled');
          }
        },
        {
          id: 'ok-rename-btn',
          label: 'OK',
          color: 'primary',
          closesModal: true,
          getData: true,
          click: (data: any) => {
            if (data?.isValid && data?.hasChanged) {
              this.performRename(entity, data.newName);
            }
          },
          isEnabled: (data: any) => {
            return data?.isValid === true;
          }
        }
      ]
    };

    this.modalService.show(config);
  }

  private performRename(entity: IBizEntity, newName: string): void {

    const updateEntity: IBizEntity = {
      ...entity,
      name: newName
    };

    this.bizEntityStateStoreService.update(updateEntity, false)?.then((res) => {
      if (res) {
        this.logger.info(`✅ Entity renamed successfully to: ${newName}`);
        this.toastService.toast(`Renamed to "${newName}" successfully.`);
        // Trigger refresh of the UI by notifying that an entity was updated
        // Pass the updated entity so we can handle structural vs operational differently
        this.global.setBizEntityUpdated(true, updateEntity);
      }
    }).catch((err) => {
      this.logger.error(`❌ Rename error: ${err}`);
      this.toastService.toast('Failed to rename. Please try again.');
    });
  }

  openBizEntityProperties() {
    this.global.setPropertiesOpenClicked(true);
  }

  openBizEntity(entity: any) {
    if (!entity) {
      this.logger.error('No entity to open');
      return;
    }
    this.logger.info(`📂 Opening entity: ${entity.name || entity.id}`);
    this.global.setOpenEntityClicked(true, entity);
  }

  unpinFromDashboard(entity: any) {
    if (!entity || !entity.id) {
      this.logger.error('No valid entity to unpin');
      return;
    }

    const activeDashboardId = this.dashboardService.activeDashoardTabId;
    if (!activeDashboardId) {
      this.logger.error('No active dashboard found');
      return;
    }

    // Find the dashboard
    const dashboard = this.dashboardService.dashboardList.find(
      dash => dash.id.toString() === activeDashboardId
    );

    if (!dashboard) {
      this.logger.error('Active dashboard not found in dashboard list');
      return;
    }

    // Find the layout item that contains this entity
    const layoutItem = dashboard.layoutList?.find(
      item => item.id === entity.id ||
        (dashboard.items?.find(dashItem => dashItem.id === item.id)?.content?.data?.id === entity.id)
    );

    if (!layoutItem) {
      this.logger.error(`Layout item not found for entity ${entity.id} in dashboard ${activeDashboardId}`);
      this.toastService.toast(`Could not find item to unpin`);
      return;
    }

    this.logger.info(`🔓 Unpinning layout item ${layoutItem.id} (entity: ${entity.name}) from dashboard ${activeDashboardId}`);

    // Cache has been removed - entity data will be fetched fresh when needed
    this.dashboardService.removeDashboardItem(layoutItem.id, activeDashboardId);
  }

  getDashboardContextMenuItemList(data: any): ContextMenuItem[] {
    const entity = data && typeof data === 'object' ? data : undefined;

    // Get the current dashboard ID and find the layout item for this entity
    const activeDashboardId = this.dashboardService.activeDashoardTabId;
    const dashboard = this.dashboardService.dashboardList.find(
      dash => dash.id.toString() === activeDashboardId
    );

    // Find the layout item that corresponds to this entity
    const layoutItem = dashboard?.layoutList?.find(
      item => item.id === entity?.id ||
        (dashboard.items?.find(dashItem => dashItem.id === item.id)?.content?.data?.id === entity?.id)
    );

    const baseMenuItems = [
      // {
      //   label: ContextMenuLabels.Rename,
      //   icon: ContextMenuIcons.Rename,
      //   // disabled: data?.rights?.update ? false : true,
      //   // temporarily disable rename in dashboard context menu until we handle the complexity of renaming structural entities that are pinned to dashboard (since the rename affects both the entity and the layout item, and we need to ensure the UI updates correctly after)
      //   disabled: true
      // },
      // {
      //   separator: true
      // },
      // {
      //   label: ContextMenuLabels.RemovePin,
      //   icon: ContextMenuIcons.Delete,
      //   // disabled: data?.rights?.delete ? false : true,
      //   command: () => { this.deleteBizEntity(data) },
      //        // temporarily disable rename in dashboard context menu until we handle the complexity of renaming structural entities that are pinned to dashboard (since the rename affects both the entity and the layout item, and we need to ensure the UI updates correctly after)
      //   disabled: true
      // },
      // {
      //   separator: true
      // },
      {
        label: ContextMenuLabels.Unpin_From_Dashboard,
        icon: ContextMenuIcons.Unpin,
        command: () => { this.unpinFromDashboard(data) }
      },
      {
        separator: true
      },
      {
        label: ContextMenuLabels.RevealInExplorer,
        icon: ContextMenuIcons.View,
        command: () => {
          if (!data || !data.id) {
            this.logger.error('No valid entity to view in explorer');
            return;
          }
          this.logger.info(`Navigating to library explorer with entity: ${data.name || data.id}`);
          
          // Navigate first so the subscription is ready before the ID is emitted
          this.router.navigate(['/libraries']);
          this.appStateService.incomingBizEntitySelectionId = data.id.toString();
        }
      },
      // {
      //   separator: true
      // },
      {
        label: ContextMenuLabels.View,
        icon: ContextMenuIcons.View,
        disabled: data?.rights?.read ? false : true,
        command: () => { this.openBizEntity(data) }
      },
    ];

    // Add "Move To Dashboard" submenu at the top (position 0, notAtDashboard = false since we're on dashboard)
    // Pass the entityContainerId (the layout item id in the source tab) and entityContainerItem (layout item)
    return this.getOptionMenuSubList(
      baseMenuItems,
      false,
      0,
      entity,
      false,
      layoutItem?.id,
      layoutItem
    );
  }

  private getFilenameWithExtension(name: string, category?: number): string {
    // If the name already has a recognized extension, return as-is
    if (name.includes('.') && name.lastIndexOf('.') > 0) {
      return name;
    }
    // Append the default extension for the entity's category
    switch (category) {
      case BizEntityTypeCategory.HayagrivaFileDoc:
        return name + FileExtensions.DOCX;
      case BizEntityTypeCategory.HayagrivaFileSpreadsheet:
        return name + FileExtensions.XLSX;
        case BizEntityTypeCategory.HayagrivaFileCSV:
          return name + FileExtensions.CSV;
      case BizEntityTypeCategory.HayagrivaFilePresentation:
        return name + FileExtensions.PPTX;
      case BizEntityTypeCategory.HayagrivaFileDrawing:
        return name + FileExtensions.ODG;
      case BizEntityTypeCategory.HayagrivaFileMarkdown:
        return name + FileExtensions.MD;
      case BizEntityTypeCategory.HayagrivaFilePDF:
        return name + FileExtensions.PDF;
      default:
        return name;
    }
  }

  downloadDocument(data: IBizEntity) {
    if (!data || !data.id) {
      this.logger.error('No valid entity to download');
      return;
    }
    this.entityFileUrlService.getFileUrl(data.id).then((file) => {
      const url = file?.fileUrl?.[0];
      if (url) {
        // Ensure the filename includes the correct extension so re-uploads are not blocked
        const baseName = data.name ? `${data.name}` : `document-${data.id}`;
        const filename = this.getFilenameWithExtension(baseName, data.type?.category);
        this.triggerFileDownload(url, filename).then(() => {
          this.logger.info(`⬇️ Download triggered for entity: ${filename} (${url})`);
        }).catch((error) => {
          this.logger.error(`Error triggering download: ${error}`);
          this.toastService.toast('Failed to download file. Please try again.');
        });
      } else {
        this.logger.error('No file URL found for download');
        this.toastService.toast('No file available for download.');
      }
    }).catch((err) => {
      this.logger.error(`Error fetching file URL for download: ${err}`);
      this.toastService.toast('Failed to download file. Please try again.');
    });
  }

  private async triggerFileDownload(url: string, filename: string): Promise<void> {
    try {
      // Convert remote URL to a blob URL so browsers honor download for cross-origin links.
      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.status}`);
      }

      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      this.clickDownloadLink(objectUrl, filename);
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      this.logger.warn(`Falling back to direct download URL: ${error}`);
      this.clickDownloadLink(url, filename);
    }
  }

  private clickDownloadLink(href: string, filename: string): void {
    const link = document.createElement('a');
    link.href = href;
    link.download = filename;
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  /**
   * Build dynamic "New" menu items for a structural entity
   * SINGLE SOURCE OF TRUTH: Delegates to LibraryMenuService.buildContextMenuNewItems
   * to ensure consistency and avoid code duplication
   */
  private getDynamicNewActionCallbacks() {
    return {
      onFileUpload: (item: any, event: any) => {
        this.logger.info(`📁 File upload triggered for: ${item.name}`);
        this.appStateService.selectedBizEntityForEdit = item;
        this.global.requestMenuAction('upload', item, undefined, event);
      },
      onNewFileCreation: (item: any, event: any) => {
        this.logger.info(`📄 New file creation triggered for: ${item.name}`);
        this.global.requestMenuAction('new-file', item, undefined, event);
      },
      onCreateFolder: (item: any, event: any) => {
        this.logger.info(`📁 Create folder triggered for: ${item.name}`);
        this.global.setIsFolderOpen(true);
      },
      onNewFromTemplate: (item: any, category: any, event: any) => {
        this.logger.info(`📋 New from template triggered for category: ${category}`);
        this.global.requestMenuAction('new-from-template', item, Number(category), event);
      }
    };
  }

  private buildDynamicNewMenuItemsSync(parentEntity: IBizEntity): ContextMenuItem[] {
    try {
      const libraryMenuService = this.injector.get(LibraryMenuService);
      const callbacks = this.getDynamicNewActionCallbacks();

      return libraryMenuService.buildContextMenuNewItemsSync(
        parentEntity,
        callbacks.onFileUpload,
        callbacks.onNewFileCreation,
        callbacks.onCreateFolder,
        callbacks.onNewFromTemplate
      ) as ContextMenuItem[];
    } catch (error) {
      this.logger.error(`Error building sync dynamic new menu items: ${error}`);
      return [];
    }
  }

  private async buildDynamicNewMenuItemsAsync(parentEntity: IBizEntity): Promise<ContextMenuItem[]> {
    try {
      // Use Injector to get LibraryMenuService at runtime (avoids circular dependency)
      const libraryMenuService = this.injector.get(LibraryMenuService);
      const callbacks = this.getDynamicNewActionCallbacks();
      
      // Use LibraryMenuService as the single source of truth for menu building
      // This ensures the same logic is used everywhere
      return await libraryMenuService.buildContextMenuNewItems(
        parentEntity,
        callbacks.onFileUpload,
        callbacks.onNewFileCreation,
        callbacks.onCreateFolder,
        callbacks.onNewFromTemplate
      );
    } catch (error) {
      this.logger.error(`Error building dynamic new menu items: ${error}`);
      return [];
    }
  }

  private buildNewMenuWrapper(data: any, newMenuItems: ContextMenuItem[]): ContextMenuItem | null {
    if (!newMenuItems.length) {
      return null;
    }

    const isLibraryContext = data?.type?.category === BizEntityTypeCategory.Tenant;
    const hasAnyCreatePermission = newMenuItems.some((item) => item.disabled !== true);

    return {
      label: ContextMenuLabels.New,
      icon: isLibraryContext ? ContextMenuIcons.NewBook : ContextMenuIcons.NewFolder,
      disabled: !hasAnyCreatePermission,
      items: newMenuItems
    };
  }

  private buildNewMenuLoadingWrapper(data: any): ContextMenuItem {
    const isLibraryContext = data?.type?.category === BizEntityTypeCategory.Tenant;
    return {
      label: ContextMenuLabels.New,
      icon: isLibraryContext ? ContextMenuIcons.NewBook : ContextMenuIcons.NewFolder,
      disabled: false,
      items: [
        {
          label: '...loading',
          disabled: true,
          command: () => { }
        }
      ]
    };
  }

  /**
   * Fast Discussion item for immediate menu paint.
   * The async menu pass replaces this with accurate enabled/submenu behavior.
   */
  private buildDiscussionQuickMenuItem(currentEntity?: IBizEntity): ContextMenuItem {
    return {
      label: UILabels.DISCUSSION,
      icon: ContextMenuIcons.Comments,
      disabled: true,
      command: () => { }
    };
  }

  getContextMenuItemList(data: any): ContextMenuItem[] {
    const menuItems: ContextMenuItem[] = [];
    if (data?.type?.functionType === BizEntityFunctionTypes.Structural) {
      const libraryMenuService = this.injector.get(LibraryMenuService);
      const cacheReady = libraryMenuService.isContextMenuNewItemsCacheReady();

      const newMenuItems = this.buildDynamicNewMenuItemsSync(data);
      const newMenuWrapper = this.buildNewMenuWrapper(data, newMenuItems)
        ?? (!cacheReady ? this.buildNewMenuLoadingWrapper(data) : null);
      if (newMenuWrapper) {
        menuItems.push(newMenuWrapper);
      }
    }

    menuItems.push({
        label: ContextMenuLabels.Edit,
        icon: ContextMenuIcons.Edit,
        command: () => {
          // Ensure the entity to edit is available to the form via app state.
          // `selectedBizEntityForEdit` is set when the context menu is opened
          // (see `onContextMenuClicked` / `onNavItemRightClick` in libraries page).
          // Fall back to `data` in case the caller passed the entity directly.
          const entityToEdit = this.appStateService.selectedBizEntityForEdit ?? (data as IBizEntity | undefined);
          if (entityToEdit) {
            this.appStateService.selectedBizEntityForEdit = entityToEdit;
          } else {
            this.logger.warn('Edit clicked but no entity is selected');
            return;
          }
          this.global.setIsEditEntityOpen(true);
        },
           disabled: data != undefined && data?.rights?.update ? false : true,
        // disabled: data?.rights?.edit ? false : true,
    })
    menuItems.push(
      {
        label: ContextMenuLabels.View,
        icon: ContextMenuIcons.View,
        command: () => { this.openBizEntity(this.appStateService.selectedBizEntityForEdit) },
        disabled: data?.rights?.read ? false : true,
      },
      // {
      //   label: 'Open in Collabora',
      //   icon: 'cilPencil',
      //   command: () => { /* TODO: Implement Collabora open functionality */ }
      // },
      ...(data?.type?.functionType === BizEntityFunctionTypes.Operational ? [{
        label: ContextMenuLabels.Download,
        icon: ContextMenuIcons.Download,
        disabled: data?.rights?.read ? false : true,
        command: () => {
          // Resolve at click time to avoid stale closure issues when menu items are reused.
          const entityToDownload = this.appStateService.selectedBizEntityForEdit ?? data;
          this.downloadDocument(entityToDownload as IBizEntity);
        }
      }] : []),

      {
        label: ContextMenuLabels.Share,
        icon: ContextMenuIcons.Share,
        // disabled: data?.rights?.share ? false : true,
        disabled: true,
        command: () => { /* TODO: Implement share functionality */ }
      }
    );

    // Add separator before permissions-based actions
    menuItems.push({
      separator: true
    });

    // Add Rename - always visible, disabled based on user permissions
    menuItems.push({
      label: ContextMenuLabels.Rename,
      icon: ContextMenuIcons.Rename,
      disabled: data != undefined && data?.rights?.update ? false : true,
      command: () => { this.updateBizEntityName(data) }
    });

    // Show Discussion immediately in the sync menu.
    menuItems.push(this.buildDiscussionQuickMenuItem(data as IBizEntity | undefined));

    // Add Delete - always visible, disabled based on user permissions
    menuItems.push({
      label: ContextMenuLabels.Delete,
      icon: ContextMenuIcons.Delete,
      disabled: data != undefined && data?.rights?.delete ? false : true,
      command: () => {
        // Use the currently selected entity from app state, which should be set by onContextMenuClicked
        const entityToDelete = this.appStateService.selectedBizEntityForEdit;
        if (entityToDelete) {
          this.deleteBizEntity(entityToDelete);
        } else {
          this.logger.error('⚠️ No entity selected for deletion');
        }
      }
    });

    return menuItems;
  }

  /**
   * Get context menu item list with Discussion menu item (async version)
   */
  async getContextMenuItemListAsync(data: any, includeDiscussion: boolean = true): Promise<ContextMenuItem[]> {
    const menuItems: ContextMenuItem[] = [];
    // Add New button at the top for Structural entities
    if (data?.type?.functionType === BizEntityFunctionTypes.Structural) {
      // Prefer sync cached items to keep submenu stable while menu is open.
      // Fall back to async only when cache is not ready.
      let newMenuItems = this.buildDynamicNewMenuItemsSync(data);
      if (!newMenuItems.length) {
        newMenuItems = await this.buildDynamicNewMenuItemsAsync(data);
      }
      const newMenuWrapper = this.buildNewMenuWrapper(data, newMenuItems);
      if (newMenuWrapper) {
        menuItems.push(newMenuWrapper);
      }
    }
    menuItems.push({
        label: ContextMenuLabels.Edit,
        icon: ContextMenuIcons.Edit,
        command: () => {
          const entityToEdit = this.appStateService.selectedBizEntityForEdit ?? (data as IBizEntity | undefined);
          if (entityToEdit) {
            this.appStateService.selectedBizEntityForEdit = entityToEdit;
          } else {
            this.logger.warn('Edit clicked but no entity is selected');
            return;
          }
          this.global.setIsEditEntityOpen(true);
        },
           disabled: data != undefined && data?.rights?.update ? false : true,
    })
    menuItems.push(
      {
        label: ContextMenuLabels.View,
        icon: ContextMenuIcons.View,
        command: () => { this.openBizEntity(this.appStateService.selectedBizEntityForEdit) },
        disabled: data?.rights?.read ? false : true,
      },
      ...(data?.type?.functionType === BizEntityFunctionTypes.Operational ? [{
        label: ContextMenuLabels.Download,
        icon: ContextMenuIcons.Download,
        disabled: data?.rights?.read ? false : true,
        command: () => {
          const entityToDownload = this.appStateService.selectedBizEntityForEdit ?? data;
          this.downloadDocument(entityToDownload as IBizEntity);
        }
      }] : []),

      {
        label: ContextMenuLabels.Share,
        icon: ContextMenuIcons.Share,
        disabled: true,
        command: () => { /* TODO: Implement share functionality */ }
      }
    );

    // Add separator before permissions-based actions
    menuItems.push({
      separator: true
    });

    // Add Rename - always visible, disabled based on user permissions
    menuItems.push({
      label: ContextMenuLabels.Rename,
      icon: ContextMenuIcons.Rename,
      disabled: data != undefined && data?.rights?.update ? false : true,
      command: () => { this.updateBizEntityName(data) }
    });

    // Add Discussion menu item with submenu support (for both operational and structural entities)
    if (includeDiscussion) {
      const discussionMenuItem = await this.getDiscussionMenuItem(data);
      menuItems.push(discussionMenuItem ?? this.buildDiscussionQuickMenuItem(data as IBizEntity | undefined));
    }

    // Add Delete - always visible, disabled based on user permissions
    menuItems.push({
      label: ContextMenuLabels.Delete,
      icon: ContextMenuIcons.Delete,
      disabled: data != undefined && data?.rights?.delete ? false : true,
      command: () => {
        const entityToDelete = this.appStateService.selectedBizEntityForEdit;
        if (entityToDelete) {
          this.deleteBizEntity(entityToDelete);
        } else {
          this.logger.error('⚠️ No entity selected for deletion');
        }
      }
    });

    return menuItems;
  }

  /**
   * Get Discussion menu item with submenu for multiple channels
   */
  private async getDiscussionMenuItem(currentEntity?: IBizEntity): Promise<ContextMenuItem | null> {
    if (!currentEntity || !currentEntity.id) {
      return null;
    }

    let isDisabled = false;
    try {
      await this.relationshipState.getState(
        [currentEntity.id],
        this.bizEntityTypeStateStoreService,
        undefined,
        undefined,
        true
      );
    } catch (error) {
      isDisabled = true;
    }

    // Get discussion channel items
    const discussionChannelItems = await this.getDiscussionChannelItems(currentEntity);
    this.logger.info(`Discussion channel items: ${discussionChannelItems.length}`);

    // Build the Discussion menu item
    const discussionMenuItem: ContextMenuItem = {
      label: UILabels.DISCUSSION,
      icon: ContextMenuIcons.Comments,
      // If there's only one channel, handle click directly; otherwise show submenu
      ...(discussionChannelItems.length === 1
        ? {
          command: async () => {
            this.logger.info(`Single discussion channel clicked`);
            const res = await this.relationshipState.getState(
              [currentEntity!.id],
              this.bizEntityTypeStateStoreService,
              undefined,
              undefined,
              true
            );
            if (res && res.length > 0) {
              for (const relationship of res) {
                for (const target of relationship.to) {
                  if (target.target.category === BizEntityTypeCategory.VaicChannel) {
                    const bizEntity = target.target.bizEntity as any;
                    this.xappCapHandlerService.handleXappEntityClick(bizEntity);
                    return;
                  }
                }
              }
            }
          }
        }
        : { items: discussionChannelItems }),
      disabled: isDisabled || discussionChannelItems.length === 0
    };

    return discussionMenuItem;
  }

  /**
   * Get discussion channel submenu items
   */
  private async getDiscussionChannelItems(currentEntity?: IBizEntity): Promise<ContextMenuItem[]> {
    if (!currentEntity || !currentEntity.id) {
      return [];
    }

    try {
      const res = await this.relationshipState.getState(
        [currentEntity.id],
        this.bizEntityTypeStateStoreService,
        undefined,
        undefined,
        true
      );

      if (!res || res.length === 0) {
        return [];
      }

      // Collect all VaicChannel entities
      const channels: { bizEntity: any; name: string }[] = [];
      res.forEach((relationship) => {
        relationship.to.forEach((target) => {
          if (target.target.category === BizEntityTypeCategory.VaicChannel) {
            const bizEntity = target.target.bizEntity;
            const name = (typeof bizEntity === 'object' && bizEntity?.name) || 'Channel';
            channels.push({ bizEntity, name });
          }
        });
      });

      // Return submenu items for each channel
      return channels.map((channel) => ({
        label: channel.name,
        icon: ContextMenuIcons.Comment,
        command: () => {
          this.logger.info(`Discussion channel clicked: ${channel.name}`);
          this.xappCapHandlerService.handleXappEntityClick(channel.bizEntity);
        }
      }));
    } catch (error) {
      this.logger.error(`Error fetching discussion channels: ${error}`);
      return [];
    }
  }

  private subscribeToStates() {
    if (this.dashboardStateStoreService.changed) {
      this.dashboardStateSubscription = this.dashboardStateStoreService.changed.subscribe(() => {
        this.populateDashboardListFromRemote();
      });
    }
  }

  optionMenuSubList: iDashboard[] = this.getDashboardList();
  optionMenuSubListObject: { label: string; command: Function; styleClass?: string; disabled?: boolean }[] = [];
  getOptionMenuSubList(optionMenuList: any, isDisabledProperty: boolean = false, position?: number, entity?: IBizEntity, notAtDashboard: boolean = true, entityContainerId?: string, entityContainerItem?: iLayoutList) {
    if (!entity) {
      entity = this.appStateService.selectedBizEntityForEdit || undefined;
    }
    let label = notAtDashboard ? 'Pin To Dashboard' : 'Move To Dashboard';
    let level = 'default';
    this.optionMenuSubListObject = [];

    let list = this.optionMenuSubList;

    if (this.router.url.includes('dashboard')) {
      notAtDashboard = false;
    }

    if (!notAtDashboard) {
      list = list.filter(item => item.label !== this.dashboardService.dashboardList.find(dashboard => dashboard.id === this.dashboardService.activeDashoardTabId)?.label);
    }
    // Don't capture the entity here - get it fresh when the command is executed
    // This ensures we always pin the currently selected entity from the context menu
    list.forEach((item) => {
      this.optionMenuSubListObject.push({
        label: item.label,
        command: () => {
          // Get the fresh entity from appStateService at execution time
          const currentEntity = this.appStateService.selectedBizEntityForEdit || undefined;
          if (notAtDashboard) {
            this.pinToDashboard(item, currentEntity);
          } else {
            this.moveToDashboard(item, currentEntity, entityContainerId, entityContainerItem);
          }
        },
      });
    });
    const newMenuItem = {
      id: 'PinToDashboard',
      label: label,
      icon: notAtDashboard ? ContextMenuIcons.Thumbtack : ContextMenuIcons.MoveToDashboard,
      items: isDisabledProperty ? this.optionMenuSubListObject : [],
      disabled: this.optionMenuSubListObject.length <= 0 ? true : false,
    };

    // Insert at position if provided, else push to the end
    if (typeof position === 'number' && position >= 0 && position < optionMenuList.length) {
      optionMenuList.splice(position, 0, newMenuItem);
    } else {
      optionMenuList.push(newMenuItem);
    }

    optionMenuList.forEach((item: any) => {
      if (item.label === label) {
        item.items = this.optionMenuSubListObject;
      }
    });

    return optionMenuList;
  }

  populateDashboardListFromRemote() {
    this.dashboardStateStoreService.getState(undefined, undefined, this.pinStateStoreService).then((response) => {
      if (response) {
        this.dashboardService.dashboardList = response;
        this.optionMenuSubList = this.getDashboardList();
      }
    });
  }

  getDashboardList() {
    let dashboardMenuList = this.dashboardService.dashboardList.map((group) => {

      return group;
    }).sort((a, b) => {
      return a.label.localeCompare(b.label);
    });

    let dashboardMenuNameList = dashboardMenuList.map((dashboard) => {
      return dashboard;
    });
    return dashboardMenuNameList;
  }

  pinToDashboard(dashboard: iDashboard, entity?: IBizEntity) {
    // Get the current user's ID
    const currentUser = this.authService.getCurrentUser();
    const ownerId = currentUser?.id || '19'; // Fallback to '19' if no user

    let dimensions: iContentDimensions = {
      "w": 1, "h": 3, "minW": 1, "minH": 3
    };
    let pinnedContainer: iPinnedItemContainer<iDashboardItem> = {
      id: faker.number.int().toString(),
      uuid: faker.string.uuid(),
      type: PinItemContainerType.Dashboard,
      location: PinItemLocations.Dashboard,
      locationId: dashboard.id,
      pinnedItems: [],
      ownerId: ownerId
    }

    this.logger.info(`📌 Pinning entity ${entity?.name} to dashboard ${dashboard.label} for owner ${ownerId}`);
    this.dashboardService.addItemToDashboard(dashboard, entity, dimensions)
  }

  moveToDashboard(dashboard: iDashboard, entity?: IBizEntity, entityContainerId?: string, entityContainerItem?: iLayoutList) {
    if(!entityContainerId || !entityContainerItem){
      return;
    }
    
    // Use the provided entity or fall back to the selected entity from app state
    const entityToMove = entity || this.appStateService.selectedBizEntityForEdit;
    
    if (!entityToMove) {
      console.error('[ContextMenuService] No entity available to move to dashboard');
      return;
    }
    
    let dimensions:iContentDimensions ={
      "w": 1, "h": 3, "minW": 1, "minH": 3
    };
    let pinnedContainer : iPinnedItemContainer<iDashboardItem> = {
      id: faker.number.int().toString(),
      uuid: faker.string.uuid(),
      type: PinItemContainerType.Dashboard,
      location: PinItemLocations.Dashboard,
      locationId: dashboard.id,
      pinnedItems: [],
      ownerId: '19'
    }
 
   this.dashboardService.moveItemToDashboard(dashboard, entityToMove, dimensions, entityContainerId, entityContainerItem) 
  }
}
